import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import { RootState } from '../../app/store';
import {
  setBillingError,
  setBillingLoading,
  setBillingPlan,
  setUsageData
} from '../../features/integrations/billingSlice';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';
import BillingHistory from '../Reports/BillingHistory';
import AvailablePlans from './AvailablePlans';
import BillingInformation from './BillingInformation';

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// interface BillingPlan {
//   tenantId: string;
//   planCode: string;
//   status: string;
//   trialEndsAt: string | null;
//   currentPeriodStart: string;
//   currentPeriodEnd: string;
//   limitsJson: string | null;
//   usageJson: string | null;
//   updatedAt: string;
// }

const tabs = [
  'Your plan',
  'Billing Information',
  'Billing History',
  'Available Plans',
];

// Upgrade Plan Modal Component
interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (planCode: string) => void;
  currentPlan: string;
  loading: boolean;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  currentPlan,
  loading,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<string>(currentPlan);
  const [isChanged, setIsChanged] = useState(false);

  const plans = [
    { code: 'FREE', name: 'Free', price: '$0/mo' },
    { code: 'PREMIUM', name: 'Premium', price: '$29/mo' },
    { code: 'ENTERPRISE', name: 'Enterprise', price: 'Custom' },
  ];

  useEffect(() => {
    setSelectedPlan(currentPlan);
    setIsChanged(false);
  }, [currentPlan, isOpen]);

  const handlePlanChange = (planCode: string) => {
    setSelectedPlan(planCode);
    setIsChanged(planCode !== currentPlan);
  };

  const handleUpgrade = () => {
    if (isChanged && selectedPlan !== currentPlan) {
      onUpgrade(selectedPlan);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
      <div className='bg-white rounded-lg shadow-lg w-[500px] p-6 relative'>
        <button
          onClick={onClose}
          disabled={loading}
          className='absolute top-4 right-4 text-gray-500 hover:text-gray-700 disabled:opacity-50'
        >
          <FiXComponent size={24} />
        </button>

        <h2 className='text-[24px] font-bold text-[#3F5265] mb-2'>Upgrade Plan</h2>
        <p className='text-[#657584] text-[16px] mb-6'>
          Select a new plan for your account
        </p>

        <div className='space-y-3 mb-6'>
          {plans.map((plan) => (
            <label
              key={plan.code}
              className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${selectedPlan === plan.code
                ? 'border-[#00899E] bg-[#F0F9FA]'
                : 'border-gray-200 hover:border-gray-300'
                }`}
            >
              <div className='flex items-center gap-3'>
                <input
                  type='radio'
                  name='plan'
                  value={plan.code}
                  checked={selectedPlan === plan.code}
                  onChange={() => handlePlanChange(plan.code)}
                  disabled={loading}
                  className='w-4 h-4 text-[#00899E] focus:ring-[#00899E]'
                />
                <div>
                  <span className='font-semibold text-[#3F5265]'>{plan.name}</span>
                </div>
              </div>
              {plan.code === currentPlan && (
                <span className='text-xs bg-[#ECFDF5] text-[#07A26F] px-2 py-1 rounded-full'>
                  Current
                </span>
              )}
            </label>
          ))}
        </div>

        <div className='flex justify-end gap-3'>
          <button
            onClick={onClose}
            disabled={loading}
            className='px-4 py-2 border rounded-md text-[#3F5265] font-medium hover:bg-gray-100 disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            onClick={handleUpgrade}
            disabled={!isChanged || loading}
            className={`px-6 py-2 rounded-md text-white font-medium transition-colors ${!isChanged || loading
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-[#FF5722] hover:bg-[#e04a1c]'
              }`}
          >
            {loading ? 'Upgrading...' : 'Upgrade Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

const YourPlan: React.FC = () => {
  const dispatch = useDispatch();
  const { plan: billingPlan, usageData, loading } = useSelector(
    (state: RootState) => state.billing
  );

  const [activeTab, setActiveTab] = useState('Your plan');
  const [selected, setSelected] = useState<string[]>([]);
  const [exportPDF, setExportPDF] = useState<(() => void) | null>(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const generateIdempotencyKey = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (endDate: string): number => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const fetchBillingPlan = async () => {
    try {
      dispatch(setBillingLoading(true));
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        dispatch(setBillingLoading(false));
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/billing/plan`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch billing plan: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && data.data) {
        dispatch(setBillingPlan(data.data));

        // Update usage data if available
        if (data.data.usageJson && data.data.usageJson !== 'null') {
          try {
            const usage = JSON.parse(data.data.usageJson);
            dispatch(setUsageData([
              { label: 'Audience', used: usage.audience || 0, total: 500 },
              { label: 'Campaigns', used: usage.campaigns || 0, total: 50 },
              { label: 'Automation', used: usage.automation || 0, total: 0 },
              { label: 'Templates', used: usage.templates || 0, total: 0 },
            ]));
          } catch (e) {
            // console.error('Error parsing usage data:', e);
            toast.error('Error parsing usage data');
          }
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      dispatch(setBillingError(error.message || 'Failed to load billing plan'));
      toast.error(error.message || 'Failed to load billing plan');
      // console.error('Fetch billing plan error:', error);
    }
  };

  const handleUpgradePlan = async (planCode: string) => {
    try {
      setUpgradeLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/billing/upgrade`, {
        method: 'POST',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planCode }),
      });

      if (!response.ok) {
        throw new Error(`Failed to upgrade plan: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' || data.status === 'ACCEPTED') {
        toast.success(`Successfully upgraded to ${planCode} plan!`);
        setShowUpgradeModal(false);

        // Update Redux state immediately
        if (billingPlan) {
          const updatedPlan = {
            ...billingPlan,
            planCode: planCode,
          };
          dispatch(setBillingPlan(updatedPlan));
        }

      } else {
        throw new Error(data.message || 'Failed to upgrade plan');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upgrade plan');
      // console.error('Upgrade plan error:', error);
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Fetch billing plan only when "Your plan" tab is selected
  useEffect(() => {
    if (activeTab === 'Your plan') {
      fetchBillingPlan();
    }
  }, [activeTab]);

  // Format plan name for display
  const getPlanDisplayName = (planCode: string): string => {
    if (planCode === 'FREE') return 'Free Trial';
    if (planCode === 'PREMIUM') return 'Premium';
    if (planCode === 'ENTERPRISE') return 'Enterprise';
    return planCode;
  };

  return (
    <div>
      {/* Header */}
      <h2 className='text-[24px] font-bold text-[#3F5265] mb-2'>Billing</h2>
      <p className='text-[#657584] text-[16px] mb-6'>
        Easily manage your subscriptions, payments, and invoices in one place
      </p>

      {/* Tabs + Download PDF */}
      <div className='border rounded p-4 bg-white shadow-sm'>
        <div className='flex justify-between items-center mb-4'>
          {/* Tabs */}
          <div className='flex gap-6'>
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`pb-2 text-[14px] hover:text-[#00899E] ${activeTab === tab
                  ? 'border-b-2 border-[#00899E] font-semibold text-[#00899E]'
                  : 'text-gray-500'
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === 'Your plan' ? (
            loading ? (
              <div className='flex justify-center items-center py-12'>
                <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
              </div>
            ) : billingPlan ? (
              <div className='w-2/3 border border-[#E5E9EC] rounded-lg p-5 bg-white'>
                {/* Plan Header */}
                <div className='flex justify-between items-center mb-8'>
                  <div>
                    <h3 className='text-[20px] font-semibold text-[#3F5265]'>
                      {getPlanDisplayName(billingPlan.planCode)}
                    </h3>
                    {billingPlan.planCode === 'FREE' && (
                      <span className='inline-block mt-2 px-3 py-1 rounded-full text-[12px] font-medium bg-[#ECFDF5] text-[#07A26F]'>
                        {getDaysRemaining(billingPlan.currentPeriodEnd)} days left in trial
                      </span>
                    )}
                    {billingPlan.planCode !== 'FREE' && (
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-[12px] font-medium ${billingPlan.status === 'ACTIVE'
                        ? 'bg-[#ECFDF5] text-[#07A26F]'
                        : 'bg-red-100 text-red-600'
                        }`}>
                        {billingPlan.status === 'ACTIVE' ? 'Active' : billingPlan.status}
                      </span>
                    )}
                  </div>
                  <div className='text-right'>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className='mt-2 px-4 py-2 text-[#FFFFFF] bg-[#00A4BD] rounded hover:bg-[#007080] text-[12px] font-semibold transition-colors'
                    >
                      {billingPlan.planCode === 'FREE' ? 'Upgrade Plan' : 'Change Plan'}
                    </button>
                  </div>
                </div>

                {/* Usage Bars */}
                <div className='space-y-8 mb-10 px-4'>
                  {usageData.map((item, idx) => {
                    const percent =
                      item.total === 0
                        ? 0
                        : Math.round((item.used / item.total) * 100);

                    return (
                      <div key={idx}>
                        <div className='flex justify-between text-sm mb-3'>
                          <span className='text-[#3F5265] font-medium text-[14px]'>
                            {item.label}
                          </span>
                          <span className='text-[#657584] text-[12px]'>
                            {item.used} of {item.total}{' '}
                            {item.total > 0 && (
                              <span className='text-[#9FA9B2]'>
                                ({item.total - item.used} remaining)
                              </span>
                            )}
                          </span>
                        </div>
                        <div className='w-full h-2 bg-gray-200 rounded-full overflow-hidden'>
                          <div
                            className='h-full bg-[#00899E]'
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer Note - Only for FREE plan */}
                {billingPlan.planCode === 'FREE' && (
                  <div className='bg-[#E0F8FB] text-[#00899E] text-[14px] p-3 rounded-md'>
                    You've used {Math.round(
                      usageData.reduce((acc, item) => acc + (item.used / (item.total || 1)), 0) / usageData.length * 100
                    )}% of your Free Trial capacity. Don't lose your
                    work – upgrade by{' '}
                    <span className='font-semibold'>
                      {formatDate(billingPlan.currentPeriodEnd)}
                    </span> to continue
                    seamlessly.
                  </div>
                )}
              </div>
            ) : (
              <div className='text-center py-12 text-gray-500'>
                <p className='text-lg'>No billing plan found</p>
                <p className='text-sm mt-2'>Please refresh or contact support</p>
              </div>
            )
          ) : activeTab === 'Billing Information' ? (
            <BillingInformation />
          ) : activeTab === 'Billing History' ? (
            <BillingHistory
              setSelected={setSelected}
              setExportPDF={setExportPDF}
            />
          ) : activeTab === 'Available Plans' ? (
            <AvailablePlans currentPlan={billingPlan?.planCode || 'FREE'} />
          ) : (
            <div className='text-center text-[#657584] text-lg font-medium py-10'>
              Work Pending
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => !upgradeLoading && setShowUpgradeModal(false)}
        onUpgrade={handleUpgradePlan}
        currentPlan={billingPlan?.planCode || 'FREE'}
        loading={upgradeLoading}
      />

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default YourPlan;